import { supabase } from "../../../shared/supabase/client";

export type MediaStorageProvider = "supabase" | "r2" | "device";

interface SignedMediaUrl {
  url: string;
}

interface SignMediaRequest {
  action: "upload" | "download" | "delete";
  spaceId: string;
  storagePath: string;
  mimeType?: string;
  size?: number;
}

const configuredProvider = import.meta.env.VITE_MEDIA_STORAGE_PROVIDER?.trim().toLowerCase();

export const mediaStorageProvider: MediaStorageProvider = configuredProvider === "r2" ? "r2" : "supabase";

function client() {
  if (!supabase) throw new Error("Supabase 尚未配置");
  return supabase;
}

async function invokeSigner(request: SignMediaRequest) {
  const { data, error } = await client().functions.invoke<SignedMediaUrl>("media-sign", { body: request });
  if (error) throw error;
  if (!data?.url) throw new Error("照片存储服务没有返回有效地址");
  return data.url;
}

export async function getMediaUrl(provider: MediaStorageProvider, spaceId: string, storagePath: string) {
  if (provider === "device") {
    throw new Error("这张原图正在等待安全同步到当前设备");
  }
  if (provider === "r2") {
    return invokeSigner({ action: "download", spaceId, storagePath });
  }

  const { data, error } = await client().storage.from("memory-media").createSignedUrl(storagePath, 900);
  if (error) throw error;
  return data.signedUrl;
}

export async function uploadMedia(
  provider: MediaStorageProvider,
  spaceId: string,
  storagePath: string,
  file: File
) {
  if (provider === "r2") {
    const url = await invokeSigner({
      action: "upload",
      spaceId,
      storagePath,
      mimeType: file.type,
      size: file.size
    });
    const response = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file
    });
    if (!response.ok) throw new Error(`照片上传失败（${response.status}）`);
    return;
  }

  const { error } = await client().storage.from("memory-media").upload(storagePath, file, {
    contentType: file.type,
    upsert: false
  });
  if (error) throw error;
}

export async function deleteMedia(provider: MediaStorageProvider, spaceId: string, storagePath: string) {
  if (provider === "r2") {
    const url = await invokeSigner({ action: "delete", spaceId, storagePath });
    const response = await fetch(url, { method: "DELETE" });
    if (!response.ok && response.status !== 404) throw new Error(`照片清理失败（${response.status}）`);
    return;
  }

  const { error } = await client().storage.from("memory-media").remove([storagePath]);
  if (error) throw error;
}
